import { useI18n } from 'vue-i18n';
import { watch } from 'vue';

// Available languages in the application
const SUPPORTED_LOCALES = ['en', 'de'];
const DEFAULT_LOCALE = 'en';

// Get browser language with fallback support
function getBrowserLanguage() {
  // Get browser language (e.g., 'en-US', 'de-DE', etc.)
  const browserLang = navigator.language || navigator.userLanguage;
  
  // Extract base language code (e.g., 'en', 'de')
  const baseLang = browserLang.split('-')[0];
  
  // Check if the browser language is supported
  if (SUPPORTED_LOCALES.includes(baseLang)) {
    return baseLang;
  }
  
  // If not supported, return default language
  return DEFAULT_LOCALE;
}

export function useLang() {
  // Get i18n instance
  const { t, locale } = useI18n();
  
  // Initialize language from localStorage or browser preference
  const savedLang = localStorage.getItem('userLanguage');
  if (savedLang && SUPPORTED_LOCALES.includes(savedLang)) {
    locale.value = savedLang;
  } else {
    locale.value = getBrowserLanguage();
    localStorage.setItem('userLanguage', locale.value);
  }
  
  // Function to switch language
  const switchLanguage = (lang) => {
    if (SUPPORTED_LOCALES.includes(lang)) {
      locale.value = lang;
      localStorage.setItem('userLanguage', lang);
      
      // Update html lang attribute
      document.documentElement.setAttribute('lang', lang);
      
      return true;
    }
    return false;
  };
  
  // Watch for language changes to update HTML attributes and localStorage
  watch(locale, (newLocale) => {
    document.documentElement.setAttribute('lang', newLocale);
    localStorage.setItem('userLanguage', newLocale);
  });
  
  return {
    t,
    locale,
    switchLanguage,
    supportedLocales: SUPPORTED_LOCALES
  };
}

// Initialize language from localStorage or browser preference
export function initLanguage() {
  // Try to get saved language preference
  const savedLang = localStorage.getItem('userLanguage');
  
  if (savedLang && SUPPORTED_LOCALES.includes(savedLang)) {
    return savedLang;
  }
  
  // If running in browser context
  if (typeof navigator !== 'undefined') {
    return getBrowserLanguage();
  }
  
  return DEFAULT_LOCALE;
}